import {
  DeviceModel,
  MeasurementImportSettingsPatchDto,
  QueryFactory,
} from '@lmt/shared-types';
import { VisitType } from '@lmt/unicorn-shared/app/components/calendar/visit/common/types';

import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { TimePicker } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/date-time/TimePicker';
import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { useAdvancedForm } from '@lmt/unicorn-shared/app/helpers/form/advanced-form';
import { handleSubmitFormError } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import clsx from 'clsx';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveVisitTypes } from '@lmt/unicorn-shared/app/features/dictionaries/selectors';
import generalStyles from '@lmt/unicorn-shared/src/app/components/tenant-settings/components/SettingsBlock.module.scss';
import styles from '@lmt/unicorn-shared/src/app/components/tenant-settings/components/measurement-import/MeasurementImportSettings.module.scss';
import { Controller } from 'react-hook-form';
import {
  requiredRule,
  positiveRule,
  maxRule,
  minRule,
} from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import { useEventCallback } from '@mui/material';
import { TenantSettingsKeys } from '../types';
import { useRegisterUnsavedChanges } from '../useRegisterUnsavedChanges';
import { validateAndDirty } from '@lmt/unicorn-shared/app/helpers/form-helper';

type Props = {
  deviceModel: DeviceModel;
};

export const MeasurementImportSettings: FC<Props> = React.memo((props) => {
  const { deviceModel } = props;
  const i18n = useTranslation();
  const i18nSettings = useScopedTranslation(
    'tenant_settings.measurement_import_settings',
  );
  const modals = useModal();
  const {
    data: importSettings,
    refetch,
    isLoading,
  } = QueryFactory.MeasurementImportSettingsQuery.useGetQuery(deviceModel);
  const visitTypes = useActiveVisitTypes();
  const translatedVisitTypes: VisitType[] = useMemo(
    () =>
      visitTypes.map((type) => ({
        id: type.id,
        label: i18n.t(type.value as any),
        specificStartTimeInSecondsSinceMidnight:
          type.specificStartTimeInSecondsSinceMidnight,
        defaultDurationInSeconds: type.defaultDurationInSeconds,
      })),
    [visitTypes],
  );

  const form = useAdvancedForm<
    Omit<MeasurementImportSettingsPatchDto, 'visitLengthInSeconds'> & {
      visitLengthInHours: number;
    }
  >(
    useCallback(async (data) => {
      try {
        await QueryFactory.MeasurementImportSettingsClient.patch(
          {
            ...data,
            visitLengthInSeconds: data.visitLengthInHours * 3600,
          },
          deviceModel,
        );
        await refetch();
      } catch (e) {
        const errorDetails = handleSubmitFormError(e, setError);
        if (errorDetails.overallServerError) {
          await modals.showError({ text: errorDetails.overallServerError });
        }
      }
    }, []),
    useMemo(() => {
      return importSettings
        ? {
            ...importSettings,
            visitLengthInHours: importSettings.visitLengthInSeconds / 3600,
          }
        : undefined;
    }, [importSettings]),
    {
      mode: 'onChange',
      shouldUnregister: false,
      defaultValues: {
        createVisitAutomatically: true,
        defaultVisitTypeId: 0,
        visitStartTimeInSecondsSinceMidnight: 0,
        visitSearchBackwardDaysCount: 5,
        visitSearchForwardDaysCount: 2,
        visitLengthInHours: 1,
      },
    },
  );
  const { setValue, setError, formState, reset, register, control } = form;

  const onReset = useEventCallback(() => {
    if (importSettings) {
      reset({
        ...importSettings,
        visitLengthInHours: importSettings.visitLengthInSeconds / 3600,
      });
    }
  });

  const setVisitStartTimeAndLength = useEventCallback(
    (visitType: VisitType) => {
      if (visitType?.specificStartTimeInSecondsSinceMidnight) {
        setValue(
          'visitStartTimeInSecondsSinceMidnight',
          visitType.specificStartTimeInSecondsSinceMidnight,
          validateAndDirty,
        );
      }
      if (visitType?.defaultDurationInSeconds) {
        setValue(
          'visitLengthInHours',
          visitType.defaultDurationInSeconds / 3600,
          validateAndDirty,
        );
      }
    },
  );

  useEffect(() => {
    onReset();
  }, [importSettings]);

  const isUnsavedChanges = useRegisterUnsavedChanges(
    TenantSettingsKeys.MeasurementImport,
    form,
  );

  const deviceTitle = DeviceModel[deviceModel].toLowerCase();

  return (
    <Loading loading={isLoading}>
      <form onSubmit={form.handleSubmitDefault}>
        <div
          className={generalStyles.container}
          data-test-id={`${deviceTitle}-import-settings-container`}
        >
          <div className={generalStyles.header}>
            <div className={generalStyles.title}>
              {i18nSettings.t(`${deviceTitle}_import_settings_title` as any)}
            </div>
            <div className={generalStyles.optionContainer}>
              <Button
                disabled={!isUnsavedChanges}
                title={i18nSettings.t('reset_button_title')}
                className={generalStyles.option}
                color={ButtonColor.Primary}
                onClick={onReset}
              />
              <SubmitButton
                disabled={!isUnsavedChanges}
                submitting={formState.isSubmitting}
                className={generalStyles.option}
                title={i18nSettings.t('submit_button_title')}
              />
            </div>
          </div>

          <div className={styles.measurementImportSettingsWrapper}>
            <div>
              <CheckBox
                title={i18nSettings.t('create_visit_automatically')}
                {...register('createVisitAutomatically')}
                className={generalStyles.checkbox}
              />
              <Field
                title={i18nSettings.t('default_visit_type')}
                className={generalStyles.field}
              >
                <Controller
                  control={control}
                  name={'defaultVisitTypeId'}
                  render={({ field: { onChange, value } }) => (
                    <div className={styles.input}>
                      <DropDownInput
                        useIdFunctionAsValue={true}
                        options={translatedVisitTypes}
                        value={value}
                        idFunction={'id'}
                        getOptionLabel={'label'}
                        required={true}
                        onValueChanged={(value) => {
                          onChange(value?.id);
                          setVisitStartTimeAndLength(value);
                        }}
                        errorText={formState.errors.defaultVisitTypeId?.message}
                      />
                    </div>
                  )}
                />
              </Field>
              <div className={generalStyles.range}>
                <Field title={i18nSettings.t('visit_start_time')}>
                  <Controller
                    control={control}
                    name={'visitStartTimeInSecondsSinceMidnight'}
                    render={({ field: { onChange, value } }) => (
                      <TimePicker
                        variant={'normal'}
                        timeInMills={
                          value !== undefined ? value * 1000 : undefined
                        }
                        onTimeChanged={(value) =>
                          onChange(value ? value / 1000 : value)
                        }
                        timeEntriesIntervalInMinutes={60}
                        errorText={
                          formState.errors.visitStartTimeInSecondsSinceMidnight
                            ?.message
                        }
                      />
                    )}
                  />
                </Field>
                <Field
                  title={i18nSettings.t('visit_length')}
                  className={clsx(generalStyles.field, generalStyles.right)}
                >
                  <Input
                    variant="normal"
                    type="number"
                    step="any"
                    {...register('visitLengthInHours', {
                      ...requiredRule(),
                      ...minRule(0.1),
                      ...maxRule(100),
                      valueAsNumber: true,
                    })}
                    errorText={formState.errors.visitLengthInHours?.message}
                  />
                </Field>
              </div>
            </div>
            <div>
              <div className={styles.searchIntervalTitle}>
                {i18nSettings.t('visit_search_interval')}
              </div>
              <div className={generalStyles.row}>
                <Field
                  title={i18nSettings.t('visit_search_days_backward')}
                  className={styles.field}
                >
                  <Input
                    variant="normal"
                    type="number"
                    {...register('visitSearchBackwardDaysCount', {
                      ...requiredRule(),
                      ...positiveRule(),
                      ...maxRule(100),
                      valueAsNumber: true,
                    })}
                    errorText={
                      formState.errors.visitSearchBackwardDaysCount?.message
                    }
                  />
                </Field>
                <Field
                  title={i18nSettings.t('visit_search_days_forward')}
                  className={clsx(styles.right, styles.field)}
                >
                  <Input
                    variant="normal"
                    type="number"
                    {...register('visitSearchForwardDaysCount', {
                      ...requiredRule(),
                      ...positiveRule(),
                      ...maxRule(100),
                      valueAsNumber: true,
                    })}
                    errorText={
                      formState.errors.visitSearchForwardDaysCount?.message
                    }
                  />
                </Field>
              </div>
            </div>
            <div>
              <div className={styles.searchIntervalTitle}>
                {i18nSettings.t('case_search_interval')}
              </div>
              <div className={generalStyles.row}>
                <Field
                  title={i18nSettings.t('case_search_days_backward')}
                  className={styles.field}
                >
                  <Input
                    variant="normal"
                    type="number"
                    {...register('caseSearchBackwardDaysCount', {
                      ...requiredRule(),
                      ...positiveRule(),
                      ...maxRule(1000),
                      valueAsNumber: true,
                    })}
                    errorText={
                      formState.errors.caseSearchBackwardDaysCount?.message
                    }
                  />
                </Field>
              </div>
            </div>
            <div>
              <CheckBox
                title={i18nSettings.t('save_old_files')}
                {...register('saveOldFilesWhenMeasurementReplaced')}
                className={generalStyles.checkbox}
              />
            </div>
          </div>
        </div>
      </form>
    </Loading>
  );
});
